import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { crearOReprogramarPlanCuotas } from '../../services/cobranzaService';
import { colorPrimarioTramite } from '../Documentos/coloresTramite';
import { formatoFecha, formatoMoneda } from './PanelesCobro';
import InputMonto from '../InputMonto';
import {
    FRECUENCIAS, MAXIMO_CUOTAS, generarCuotas, hoyISO, inferirConfiguracion, repartirTotal, totalCuotas, vencimientoDeCuota,
} from './planCuotas';

const COLOR = colorPrimarioTramite('cobro');
const filaDeCuota = (c) => ({ id: c.id, monto: c.monto, fechaVencimiento: c.fechaVencimiento || '', observacion: c.observacion || '' });
const plural = (n, singular, pluralForma) => `${n} ${n === 1 ? singular : pluralForma}`;

/**
 * Crea o reprograma el plan de cuotas de un cobro. Se parte de un monto a financiar (el saldo del
 * cobro, ver montoAFinanciar) y se elige en cuántas cuotas, con qué frecuencia y desde cuándo: el
 * importe de cada cuota y sus vencimientos se calculan solos. La vista previa muestra las cuotas
 * resultantes; cada una se puede ajustar a mano con el lápiz (el cliente acuerda "la cuota 5 el
 * 28/01 y de $175.000") o sacar con el tacho.
 *
 * Nada se anula: las cuotas PENDIENTES que ya existían viajan con su `id` y se editan en el lugar
 * (ver CobroService.crearOReprogramarPlan) — cambiar una fecha no crea una cuota nueva ni deja la
 * vieja anulada. Por eso, al recalcular, las filas heredan los `id` por posición (ver
 * generarCuotas). Una cuota pendiente que no viaja se borra de verdad, y la vista previa lo avisa.
 * Las ya pagadas ni se muestran como editables ni se reenvían: quedan exactamente como están.
 *
 * `origen` dice de dónde salieron las filas de la vista previa: 'existentes' (las cuotas reales del
 * plan, al abrir: no se recalculan hasta que se toca la configuración), 'configuracion' (calculadas
 * con los campos de arriba) o 'ajustadas' (se tocó alguna a mano).
 *
 * @param montoBase  saldo a cubrir con el plan, o null si el cobro no tiene monto pactado
 */
export default function ModalPlanCuotas({ open, idCobro, cuotasVigentes = [], montoBase = null, onClose, onGuardado }) {
    const cuotasPagadas = cuotasVigentes.filter((c) => c.pagada).sort((a, b) => a.numero - b.numero);
    const cuotasPendientes = cuotasVigentes.filter((c) => !c.pagada).sort((a, b) => a.numero - b.numero);
    const tienePlanPrevio = cuotasVigentes.length > 0;

    const [config, setConfig] = useState(() => inferirConfiguracion([], hoyISO(), montoBase));
    const [filas, setFilas] = useState([]);
    const [origen, setOrigen] = useState('existentes');
    const [filaEditandose, setFilaEditandose] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (open) {
            const inicial = inferirConfiguracion(cuotasPendientes, hoyISO(), montoBase);
            // Un plan nuevo con saldo conocido arranca con la cuota única por el total, para que la vista
            // previa no esté vacía; con un plan existente arranca con las cuotas reales.
            const generadas = cuotasPendientes.length === 0 ? generarCuotas(inicial, []) : null;
            setConfig(inicial);
            setFilas(generadas ?? cuotasPendientes.map(filaDeCuota));
            setOrigen(generadas ? 'configuracion' : 'existentes');
            setFilaEditandose(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const cambiarConfig = (campo, valor) => {
        const nueva = { ...config, [campo]: valor };
        setConfig(nueva);
        const generadas = generarCuotas(nueva, cuotasPendientes);
        if (generadas) {
            setFilas(generadas);
            setFilaEditandose(null);
            setOrigen('configuracion');
        }
    };

    const actualizarFila = (idx, campo, valor) => {
        setFilas((prev) => prev.map((f, i) => (i === idx ? { ...f, [campo]: valor } : f)));
        setOrigen('ajustadas');
    };

    // Sacar o agregar una fila mantiene "Cantidad de cuotas" en sintonía, sin recalcular las demás.
    const quitarFila = (idx) => {
        const restantes = filas.filter((_, i) => i !== idx);
        setFilas(restantes);
        setConfig((prev) => ({ ...prev, cantidad: restantes.length || '' }));
        setFilaEditandose(null);
        setOrigen('ajustadas');
    };

    const agregarFila = () => {
        const ultima = filas[filas.length - 1];
        const fecha = ultima?.fechaVencimiento
            ? vencimientoDeCuota(ultima.fechaVencimiento, config.frecuencia, 1)
            : config.primerVencimiento;
        setFilas([...filas, { id: null, monto: ultima?.monto ?? '', fechaVencimiento: fecha || '', observacion: '' }]);
        setConfig((prev) => ({ ...prev, cantidad: filas.length + 1 }));
        setFilaEditandose(filas.length);
        setOrigen('ajustadas');
    };

    const handleGuardar = async () => {
        if (filas.length === 0 || filas.some((f) => !(Number(f.monto) > 0))) {
            toast.error('Cargá al menos una cuota, todas con un monto mayor a cero.');
            return;
        }

        const cuotas = filas.map((f) => ({
            id: f.id || null,
            monto: Number(f.monto),
            fechaVencimiento: f.fechaVencimiento || null,
            observacion: f.observacion?.trim() || null,
        }));

        setGuardando(true);
        try {
            await crearOReprogramarPlanCuotas(idCobro, cuotas);
            toast.success('Plan de cuotas guardado.');
            onGuardado?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo guardar el plan.');
        } finally {
            setGuardando(false);
        }
    };

    const cantidadInvalida = config.cantidad !== ''
        && (!Number.isInteger(Number(config.cantidad)) || Number(config.cantidad) < 1 || Number(config.cantidad) > MAXIMO_CUOTAS);
    const cuotasQueSeQuitan = cuotasPendientes.filter((c) => !filas.some((f) => f.id === c.id)).length;

    const totalDelPlan = totalCuotas(filas);
    const diferenciaConElSaldo = montoBase !== null && filas.length > 0 ? Math.round(totalDelPlan - montoBase) : 0;
    // Lo que lleva cada cuota, para mostrarlo mientras las filas salgan de la configuración.
    const reparto = origen === 'configuracion' ? repartirTotal(config.total, config.cantidad) : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}>
            <Box sx={{ background: `${COLOR}12`, borderBottom: `1px solid ${COLOR}33`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
                    <i className="bi bi-calendar3 me-2" style={{ color: COLOR }}></i>
                    {tienePlanPrevio ? 'Reprogramar plan de cuotas' : 'Crear plan de cuotas'}
                </span>
                <IconButton onClick={onClose} size="small" aria-label="Cerrar"><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {/* Encabezado y botones quedan fijos y solo el cuerpo se desplaza: con muchas cuotas la lista es
                más alta que la pantalla, y los botones no pueden quedar fuera de alcance. El papel del
                diálogo tiene overflow hidden (para las esquinas redondeadas), así que sin este scroll
                propio el contenido que no entra simplemente se corta. */}
            <Box sx={{ p: 3, overflowY: 'auto' }}>
                {cuotasPagadas.length > 0 && (
                    <div className="mb-3">
                        <div className="text-muted small fw-semibold mb-2">
                            Ya pagadas — no se tocan al reprogramar
                        </div>
                        <div className="d-flex flex-column gap-1">
                            {cuotasPagadas.map((c) => (
                                <div key={c.id} className="d-flex justify-content-between align-items-center rounded-3 px-3 py-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                    <span><i className="bi bi-check-circle-fill me-2" style={{ color: '#16a34a' }}></i>Cuota {c.numero}</span>
                                    <span className="fw-semibold" style={{ color: '#166534' }}>{formatoMoneda(c.monto)}</span>
                                    <span className="text-muted small">{formatoFecha(c.fechaVencimiento)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tienePlanPrevio && (
                    <div className="alert alert-info border-0 rounded-3 mb-3">
                        Estas son las cuotas pendientes actuales. Cambiá la configuración para recalcularlas, o tocá el lápiz para ajustar una en particular.
                    </div>
                )}

                <div className="fw-semibold mb-2" style={{ color: '#1e293b' }}>Nueva configuración</div>
                <div className="row g-3">
                    <div className="col-6">
                        <label htmlFor="plan-total" className="form-label small text-muted mb-1">Monto a financiar</label>
                        <InputMonto id="plan-total" placeholder="0" value={config.total} onChange={(v) => cambiarConfig('total', v)} />
                        {montoBase !== null && (
                            <div className="form-text">
                                Saldo a cubrir: {formatoMoneda(montoBase)}
                                {Number(config.total) !== montoBase && (
                                    <button type="button" className="btn btn-link btn-sm p-0 ms-2 align-baseline" onClick={() => cambiarConfig('total', montoBase)}>
                                        Usar este monto
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="col-6">
                        <label htmlFor="plan-cantidad" className="form-label small text-muted mb-1">Cantidad de cuotas</label>
                        <input
                            id="plan-cantidad" type="number" min={1} max={MAXIMO_CUOTAS}
                            className={`form-control${cantidadInvalida ? ' is-invalid' : ''}`}
                            value={config.cantidad} onChange={(e) => cambiarConfig('cantidad', e.target.value)}
                        />
                        {cantidadInvalida && <div className="invalid-feedback">Entre 1 y {MAXIMO_CUOTAS} cuotas.</div>}
                    </div>
                    <div className="col-6">
                        <label htmlFor="plan-frecuencia" className="form-label small text-muted mb-1">Frecuencia</label>
                        <select
                            id="plan-frecuencia" className="form-select"
                            value={config.frecuencia} onChange={(e) => cambiarConfig('frecuencia', e.target.value)}
                        >
                            {Object.entries(FRECUENCIAS).map(([clave, { etiqueta }]) => (
                                <option key={clave} value={clave}>{etiqueta}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-6">
                        <label htmlFor="plan-primer-vencimiento" className="form-label small text-muted mb-1">Fecha del primer vencimiento</label>
                        <input
                            id="plan-primer-vencimiento" type="date" className="form-control"
                            value={config.primerVencimiento} onChange={(e) => cambiarConfig('primerVencimiento', e.target.value)}
                        />
                    </div>
                </div>

                <hr className="my-4" />

                <div className="fw-semibold mb-2" style={{ color: '#1e293b' }}>
                    Vista previa{cuotasPagadas.length > 0 && <span className="text-muted fw-normal"> · cuotas pendientes</span>}
                    {reparto && (
                        <span className="text-muted fw-normal small ms-2">
                            · {formatoMoneda(reparto[0])} por cuota
                            {reparto[reparto.length - 1] !== reparto[0] && `, la última ${formatoMoneda(reparto[reparto.length - 1])} por el redondeo`}
                        </span>
                    )}
                </div>

                {filas.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">Completá el monto a financiar para ver las cuotas.</p>
                ) : (
                    <div className="rounded-3" style={{ border: '1px solid #e2e8f0' }}>
                        {filas.map((fila, idx) => (
                            <div
                                key={idx} className="d-flex align-items-center gap-2 px-3 py-2"
                                style={{ borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9' }}
                            >
                                <span className="text-muted fw-semibold" style={{ width: 28 }}>{idx + 1}</span>
                                {filaEditandose === idx ? (
                                    <>
                                        <input
                                            type="date" className="form-control form-control-sm" style={{ maxWidth: 170 }}
                                            aria-label={`Vencimiento de la cuota ${idx + 1}`}
                                            value={fila.fechaVencimiento} onChange={(e) => actualizarFila(idx, 'fechaVencimiento', e.target.value)}
                                        />
                                        <div className="flex-grow-1">
                                            <InputMonto
                                                size="sm" placeholder="Monto"
                                                value={fila.monto} onChange={(v) => actualizarFila(idx, 'monto', v)}
                                            />
                                        </div>
                                        <button
                                            type="button" className="btn btn-sm btn-success rounded-circle" style={{ width: 30, height: 30, padding: 0 }}
                                            aria-label={`Listo con la cuota ${idx + 1}`} onClick={() => setFilaEditandose(null)}
                                        >
                                            <i className="bi bi-check-lg"></i>
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="flex-grow-1">{formatoFecha(fila.fechaVencimiento)}</span>
                                        <span className="fw-semibold">{formatoMoneda(fila.monto)}</span>
                                        <button
                                            type="button" className="btn btn-sm btn-outline-secondary rounded-circle" style={{ width: 30, height: 30, padding: 0 }}
                                            aria-label={`Editar la cuota ${idx + 1}`} onClick={() => setFilaEditandose(idx)}
                                        >
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button" className="btn btn-sm btn-outline-danger rounded-circle" style={{ width: 30, height: 30, padding: 0 }}
                                    aria-label={`Quitar la cuota ${idx + 1}`} onClick={() => quitarFila(idx)}
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-2">
                    <button type="button" className="btn btn-link btn-sm px-0 fw-semibold text-decoration-none" onClick={agregarFila}>
                        <i className="bi bi-plus-lg me-1"></i>Agregar cuota
                    </button>
                    <span className="fw-bold" style={{ color: '#1e293b' }}>
                        {plural(filas.length, 'cuota', 'cuotas')} · Total{cuotasPagadas.length > 0 ? ' pendiente' : ''}: {formatoMoneda(totalDelPlan)}
                    </span>
                </div>

                {diferenciaConElSaldo !== 0 && (
                    <div className="alert alert-warning border-0 rounded-3 py-2 small mt-3 mb-0">
                        Las cuotas suman {formatoMoneda(Math.abs(diferenciaConElSaldo))} {diferenciaConElSaldo > 0 ? 'más' : 'menos'} que el saldo a cubrir ({formatoMoneda(montoBase)}).
                    </div>
                )}
                {cuotasQueSeQuitan > 0 && (
                    <div className="alert alert-warning border-0 rounded-3 py-2 small mt-3 mb-0">
                        Se {cuotasQueSeQuitan === 1 ? 'va a quitar' : 'van a quitar'} {plural(cuotasQueSeQuitan, 'cuota pendiente', 'cuotas pendientes')} del plan actual.
                    </div>
                )}
                {origen === 'ajustadas' && (
                    <div className="text-muted small mt-2">
                        Ajustaste cuotas a mano: si tocás la configuración de arriba, se vuelven a calcular todas.
                    </div>
                )}

            </Box>
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0, background: '#fff' }}>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={onClose} disabled={guardando}>Cancelar</button>
                <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ background: COLOR, borderColor: COLOR }} onClick={handleGuardar} disabled={guardando}>
                    {guardando ? <span className="spinner-border spinner-border-sm" /> : 'Guardar plan'}
                </button>
            </Box>
        </Dialog>
    );
}
